/**
 * The emails a deal's communications panel lists: the one it arrived on, and
 * every later email on its thread or deal group.
 *
 * Only the first used to be listed, so the reply that revised Riverbend Row's
 * ask (14 Aug) never appeared on the deal (stress test OVR-01).
 */
import type { DossierSource, EmailEnvelope } from '@/services/ingestion'

export type DealEmail = {
  from: string | null
  to: string[]
  subject: string | null
  receivedAt: string | null
}

function sender(name?: string | null, email?: string | null): string | null {
  if (name) return `${name}${email ? ` <${email}>` : ''}`
  return email ?? null
}

export function dealEmails(
  source: { emailEnvelope?: EmailEnvelope | null; receivedAt?: string | null } | undefined,
  sources: DossierSource[] | undefined,
  sourceIngestionId: string | null | undefined,
): DealEmail[] {
  const env = source?.emailEnvelope
  // Every row is dated from the record's own received time, the column the
  // later emails below are dated from. The envelope keeps the sender's offset
  // ("09:00 -0400") and the record column does not, so taking the envelope for
  // the first row alone put two clocks in one table (nd-038: Riverbend's K01
  // at 01:00 PM beside K02 at 03:10 PM for a reader outside New York).
  const own = (sources ?? []).find(
    (src) => src.sourceType === 'email' && src.ingestionId === sourceIngestionId,
  )
  const first: DealEmail[] = env
    ? [{
        from: sender(env.fromName, env.fromEmail),
        to: env.to ?? [],
        subject: env.subject ?? null,
        receivedAt: own?.receivedAt ?? source?.receivedAt ?? env.sentAt ?? null,
      }]
    : []
  const later = (sources ?? [])
    .filter((src) => src.sourceType === 'email' && src.ingestionId !== sourceIngestionId)
    .map((src) => ({
      from: sender(src.fromName, src.fromEmail),
      to: src.to ?? [],
      subject: src.subject ?? null,
      receivedAt: src.receivedAt ?? null,
    }))
  return [...first, ...later]
}
